import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useScrollLock } from './lib/scroll-lock';
import { Routes, Route, Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Linkedin, LogOut, LayoutDashboard, Settings, ShieldCheck, Menu, X } from 'lucide-react';
import { smoothScrollTo } from './lib/scroll';
import { getUser, logout } from './lib/api';
import Wordmark from './components/Wordmark';
import SignInModal from './components/SignInModal';

import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import ClaimAccount from './pages/ClaimAccount';
import Dashboard from './pages/Dashboard';
import CourseDetail from './pages/CourseDetail';
import CoursePlayer from './pages/CoursePlayer';
import LiveCoursePlayer from './pages/LiveCoursePlayer';
import EventCheckin from './pages/EventCheckin';
import PurchaseSuccess from './pages/PurchaseSuccess';
import CertVerify from './pages/CertVerify';
import Tutorials from './pages/Tutorials';
import Library from './pages/Library';
import About from './pages/About';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Account from './pages/Account';
import DripViewer from './pages/DripViewer';

import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/Dashboard';
import AdminCourses from './pages/admin/Courses';
import AdminCourseEdit from './pages/admin/CourseEdit';
import AdminUsers from './pages/admin/Users';
import AdminPurchases from './pages/admin/Purchases';
import AdminReports from './pages/admin/Reports';
import AdminCourseReports from './pages/admin/CourseReports';
import AdminDiscountCodes from './pages/admin/DiscountCodes';
import AdminUserDetail from './pages/admin/UserDetail';
import AdminPublishKeys from './pages/admin/PublishKeys';
import AdminLearnerProgress from './pages/admin/LearnerProgress';

const PRIMARY_NAV = [
  { to: '/',              label: 'Home',         end: true,  scrollTo: 'top' },
  { to: '/live-classes',  label: 'Live Classes',             scrollTo: '#live' },
  { to: '/library',       label: 'Course Library' },
  { to: '/tutorials',     label: 'Tutorials' },
  { to: '/about',         label: 'About' },
  { to: '/contact',       label: 'Contact' },
];

const HOME_PATHS = new Set(['/', '/live-classes']);

function TopNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);
  const linkRefs = useRef([]);
  const [hovered, setHovered] = useState(null);
  const [bar, setBar] = useState({ left: 0, width: 0, opacity: 0 });

  const handleClick = (e, item) => {
    if (!item.scrollTo) return;
    e.preventDefault();
    if (HOME_PATHS.has(location.pathname)) {
      // Already on a home-rendering path — update URL + smooth-scroll in place
      if (location.pathname !== item.to) navigate(item.to);
      requestAnimationFrame(() => smoothScrollTo(item.scrollTo));
    } else {
      // Cross-page — navigate to the target and pass scroll instruction
      // for Home to consume on mount.
      navigate(item.to, { state: { scrollTo: item.scrollTo } });
    }
  };

  const activeIndex = PRIMARY_NAV.findIndex((item) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to),
  );
  const target = hovered ?? activeIndex;

  useLayoutEffect(() => {
    const el = linkRefs.current[target];
    if (!el || !navRef.current || target < 0) {
      setBar((b) => ({ ...b, opacity: 0 }));
      return;
    }
    const navBox = navRef.current.getBoundingClientRect();
    const linkBox = el.getBoundingClientRect();
    setBar({
      left: linkBox.left - navBox.left,
      width: linkBox.width,
      opacity: 1,
    });
  }, [target]);

  return (
    <nav
      ref={navRef}
      className="hidden md:flex items-center gap-1 text-sm relative"
      onMouseLeave={() => setHovered(null)}
    >
      {PRIMARY_NAV.map((item, i) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          ref={(el) => (linkRefs.current[i] = el)}
          onMouseEnter={() => setHovered(i)}
          onClick={(e) => handleClick(e, item)}
          className={({ isActive }) =>
            `px-3 py-2 font-medium transition-colors ${
              isActive ? 'text-brand' : 'text-slate-600 hover:text-slate-900'
            }`
          }
        >
          {item.label}
        </NavLink>
      ))}
      <span
        key={target}
        aria-hidden
        className="pointer-events-none absolute -bottom-1 h-0.5 bg-brand rounded-full animate-grow-left"
        style={{ left: bar.left, width: bar.width, opacity: bar.opacity }}
      />
    </nav>
  );
}

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const initials = (user.full_name || user.email || '?')
    .split(/\s+/).map((w) => w[0]).join('').toUpperCase().slice(0, 2);

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full hover:bg-slate-100 pl-3 pr-1 py-1 transition-colors"
      >
        <span className="text-sm font-medium text-slate-700 hidden sm:block max-w-[140px] truncate">
          {user.full_name || user.email}
        </span>
        <span className="w-8 h-8 rounded-full bg-brand text-white text-xs font-bold flex items-center justify-center shrink-0">
          {initials}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
          <div className="px-4 py-2.5 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900 truncate">{user.full_name || 'My account'}</div>
            <div className="text-xs text-slate-500 truncate">{user.email}</div>
          </div>
          <button onClick={() => go('/dashboard')} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5">
            <LayoutDashboard size={15} className="text-slate-400" /> My dashboard
          </button>
          <button onClick={() => go('/account')} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5">
            <Settings size={15} className="text-slate-400" /> Account settings
          </button>
          {user.is_admin && (
            <button onClick={() => go('/admin')} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5">
              <ShieldCheck size={15} className="text-slate-400" /> Admin console
            </button>
          )}
          <div className="border-t border-slate-100 mt-1" />
          <button onClick={onLogout} className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2.5">
            <LogOut size={15} /> Log out
          </button>
        </div>
      )}
    </div>
  );
}

function MobileDrawer({ open, onClose, user, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { if (open) onClose(); }, [location.pathname]);

  useScrollLock(open);

  const handleNavClick = (e, item) => {
    if (!item.scrollTo) return;
    e.preventDefault();
    onClose();
    if (HOME_PATHS.has(location.pathname)) {
      if (location.pathname !== item.to) navigate(item.to);
      requestAnimationFrame(() => smoothScrollTo(item.scrollTo));
    } else {
      navigate(item.to, { state: { scrollTo: item.scrollTo } });
    }
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-slate-900/50 z-40 md:hidden" onClick={onClose} />
      )}
      <div className={`fixed top-0 right-0 h-full w-72 bg-white z-50 shadow-2xl transform transition-transform duration-300 md:hidden ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between px-5 h-16 border-b border-slate-100">
          <span className="text-sm font-bold text-slate-900">Menu</span>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>
        <nav className="px-3 py-4 space-y-1">
          {PRIMARY_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={(e) => handleNavClick(e, item)}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? 'bg-brand/10 text-brand' : 'text-slate-700 hover:bg-slate-50'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        {user && (
          <div className="border-t border-slate-100 px-3 py-4 space-y-1">
            <NavLink to="/dashboard" className="block px-4 py-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
              My dashboard
            </NavLink>
            <NavLink to="/account" className="block px-4 py-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
              Account settings
            </NavLink>
            {user.is_admin && (
              <NavLink to="/admin" className="block px-4 py-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
                Admin console
              </NavLink>
            )}
            <button onClick={onLogout} className="w-full text-left px-4 py-3 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50">
              Log out
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function PublicShell({ children }) {
  const [signInOpen, setSignInOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [user, setUser] = useState(getUser);
  const location = useLocation();

  useEffect(() => { setUser(getUser()); }, [location]);

  const handleLogout = () => {
    logout().then(() => {
      setUser(null);
      window.location.href = '/';
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* ── Header / nav ────────────────────────────────────── */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <Link to="/" aria-label="Learnable home" className="flex items-center">
            <img src="/LearnableLogo.png" alt="Learnable" className="h-9 w-auto" />
          </Link>
          <TopNav />

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <UserMenu user={user} onLogout={handleLogout} />
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="md:hidden p-2 text-slate-600 hover:text-slate-900"
                  aria-label="Open menu"
                >
                  <Menu size={22} />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setSignInOpen(true)}
                  className="text-sm font-bold bg-brand text-white px-5 py-2 rounded-md hover:bg-brand-dark"
                >
                  Sign in
                </button>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="md:hidden p-2 text-slate-600 hover:text-slate-900"
                  aria-label="Open menu"
                >
                  <Menu size={22} />
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} user={user} onLogout={handleLogout} />

      <main className="flex-1">{children}</main>

      {/* ── Footer ──────────────────────────────────────────── */}
      <footer className="bg-[#3f3f3f] text-slate-400 mt-auto">
        <div className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-4 gap-8 text-sm">
          <div>
            <Link to="/" aria-label="Learnable home" className="inline-block mb-3">
              <img src="/LearnableLogo.png" alt="Learnable" className="h-12 w-auto" />
            </Link>
            <p className="text-xs leading-relaxed mt-3">
              Practical training for counselors, driving schools, and helping
              professionals.
            </p>
          </div>

          <FooterCol title="Learn">
            <FooterLink to="/courses">Courses</FooterLink>
            <FooterLink to="/tutorials">Free tutorials</FooterLink>
            <FooterLink to="/dashboard">My dashboard</FooterLink>
          </FooterCol>

          <FooterCol title="Company">
            <FooterLink to="/about">About</FooterLink>
            <FooterLink to="/contact">Contact</FooterLink>
            <a
              href="https://www.linkedin.com/company/adeincorp/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-white transition-colors flex items-center gap-1.5"
            >
              <Linkedin size={14} /> LinkedIn
            </a>
          </FooterCol>

          <FooterCol title="Legal">
            <FooterLink to="/terms">Terms of Service</FooterLink>
            <FooterLink to="/privacy">Privacy Policy</FooterLink>
          </FooterCol>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-5 text-xs flex flex-wrap items-center justify-between gap-3">
            <div>© {new Date().getFullYear()} Learnable · Learning Linq Creative Partners LLC</div>
            <div>Operated in partnership with ADE Inc.</div>
          </div>
        </div>
      </footer>

      {signInOpen && <SignInModal onClose={() => setSignInOpen(false)} />}
    </div>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <h4 className="text-xs font-bold uppercase tracking-widest text-slate-300 mb-3">{title}</h4>
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }) {
  return (
    <li>
      <Link to={to} className="hover:text-white transition-colors">{children}</Link>
    </li>
  );
}

export default function App() {
  return (
    <Routes>
      {/* Admin gets its own layout (no public shell) */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index             element={<AdminDashboard />} />
        <Route path="courses"    element={<AdminCourses />} />
        <Route path="courses/new"        element={<AdminCourseEdit />} />
        <Route path="courses/:id/edit"   element={<AdminCourseEdit />} />
        <Route path="courses/:id/reports" element={<AdminCourseReports />} />
        <Route path="courses/:id/learners/:enrollmentId" element={<AdminLearnerProgress />} />
        <Route path="users"           element={<AdminUsers />} />
        <Route path="users/:id"       element={<AdminUserDetail />} />
        <Route path="purchases"       element={<AdminPurchases />} />
        <Route path="discount-codes"  element={<AdminDiscountCodes />} />
        <Route path="reports"         element={<AdminReports />} />
        <Route path="publish-keys"    element={<AdminPublishKeys />} />
      </Route>

      {/* Course players are full-screen — no public shell */}
      <Route path="/courses/:slug/play" element={<CoursePlayer />} />
      <Route path="/courses/:slug/live" element={<LiveCoursePlayer />} />
      <Route path="/events/:slug/checkin" element={<EventCheckin />} />

      {/* Everything else uses the marketing shell */}
      <Route path="/*" element={
        <PublicShell>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/courses" element={<Home />} />
            <Route path="/courses/:slug" element={<CourseDetail />} />
            <Route path="/live-classes" element={<Home />} />
            <Route path="/library" element={<Library />} />
            <Route path="/tutorials" element={<Tutorials />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/claim-account" element={<ClaimAccount />} />
            <Route path="/account" element={<Account />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/drip/:accessId" element={<DripViewer />} />
            <Route path="/purchase/success" element={<PurchaseSuccess />} />
            <Route path="/cert/:certNumber" element={<CertVerify />} />
          </Routes>
        </PublicShell>
      } />
    </Routes>
  );
}
